import {Warning as WarningIcon} from '@mui/icons-material';
import {Box, Button, List, Tooltip, Typography} from '@mui/material';
import {array, func, object} from 'prop-types';
import React, {useMemo} from 'react';
import {Virtuoso} from 'react-virtuoso';

import FeatureItem from './FeatureListItem.jsx';
import {getFeatureLabel} from './util.js';

const FeaturesList = ({
  features,
  selectedFeatures,
  setSelectedFeatures,
  featureProperties,
}) => {
  const nSelected = selectedFeatures.length;
  const isAllSelected = nSelected === features.length;

  const invalidCount = useMemo(
    () => features.filter(f => !f.pl_metadata.is_clean).length,
    [features],
  );

  const toggleSelectAll = () => {
    if (!isAllSelected) {
      setSelectedFeatures(features);
    } else {
      setSelectedFeatures([]);
    }
  };
  return (
    <>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          padding: theme => theme.spacing(1.5, 2),
          borderBottom: theme => `1px solid ${theme.palette.divider}`,
        }}
      >
        <Typography
          variant="subtitle2"
          sx={{flex: 1, display: 'flex', alignItems: 'center'}}
        >
          {`Selected features (${selectedFeatures.length} / ${features.length})`}
          {invalidCount > 0 && (
            <Tooltip title={`${invalidCount} features need simplification`}>
              <WarningIcon
                fontSize="small"
                color="warning"
                sx={{marginLeft: 0.5}}
              />
            </Tooltip>
          )}
        </Typography>
        <Button size="small" onClick={toggleSelectAll} color="primary">
          {isAllSelected ? 'Deselect all' : 'Select all'}
        </Button>
      </Box>
      <Box
        sx={{
          padding: 1,
          height: '100%',
          overflow: 'hidden',
        }}
      >
        <Virtuoso
          data={features.sort((a, b) => {
            return a.pl_metadata.is_clean < b.pl_metadata.is_clean ? -1 : 1;
          })}
          components={{
            // eslint-disable-next-line react/prop-types
            List: React.forwardRef(({style, children}, listRef) => {
              return (
                <List
                  style={{padding: 0, ...style, margin: 0}}
                  component="div"
                  ref={listRef}
                >
                  {children}
                </List>
              );
            }),
          }}
          itemContent={(idx, feature) => {
            const isSelected = Boolean(
              selectedFeatures.find(f => f === feature),
            );

            return (
              <FeatureItem
                key={`key-${idx}`}
                feature={feature}
                label={getFeatureLabel(feature, idx, featureProperties.title)}
                description={feature.properties[featureProperties.description]}
                id={feature.properties[featureProperties.id]}
                selected={isSelected}
                onChange={() => {
                  if (isSelected) {
                    setSelectedFeatures(
                      selectedFeatures.filter(f => f !== feature),
                    );
                  } else {
                    setSelectedFeatures([...selectedFeatures, feature]);
                  }
                }}
              />
            );
          }}
        />
      </Box>
    </>
  );
};

FeaturesList.propTypes = {
  features: array,
  selectedFeatures: array,
  setSelectedFeatures: func,
  featureProperties: object,
};

export default FeaturesList;
